import * as THREE from 'three';
import Walls from '../Walls';
import MaterialUtils from '../../helpers/MaterialUtils';

export default class StraightStair {
    constructor(scene, model) {
        this.scene = scene;
        this.model = model;
        this.treadGroup = new THREE.Group();
        this.scene.add(this.treadGroup);

        // Initialize the Walls instance ONCE to handle the side and back walls
        this.walls = new Walls(this.scene, this.createWallMaterial());

        // Default number of treads and width
        this.numTreads = 14;
        this.treadWidth = 900;

        // Create the staircase with default values (without creating new walls)
        this.createStaircase(this.numTreads);
        this.initialize();
    }
    initialize() {
        // If there's no async initialization needed, just return a resolved promise
        this.createStaircase(this.numTreads);
        return Promise.resolve();
    }
    // Function to create the staircase with a given number of treads
    createStaircase(numTreads) {
        console.log(`Creating staircase with ${numTreads} treads`);

        // Clear the previous treads (if any)
        this.clearTreads();

        const angle = THREE.MathUtils.degToRad(125); // Angle of the staircase
        const distance = 1.0; // Distance between treads

        let topTreadZOffset = 0;
        let topTreadYOffset = 0;

        for (let i = 0; i < numTreads; i++) {
            const newTread = this.model.clone(); // Clone the GLTF model for each tread
            const yOffset = distance * i * Math.sin(angle) * 0.5;
            const zOffset = -distance * i * Math.cos(angle);

            newTread.position.set(0, yOffset, zOffset);

            // Ensure the tread casts and receives shadows
            newTread.castShadow = true;
            newTread.receiveShadow = true;

            this.treadGroup.add(newTread);

            // Track the position of the last (top) tread to update the walls
            if (i === numTreads - 1) {
                topTreadYOffset = yOffset;
                topTreadZOffset = zOffset;
            }
        }

        // Update the walls after the staircase is created, passing 'straight' as the stair type
        this.walls.updateWalls(topTreadYOffset, topTreadZOffset, 'straight');
    }

    // Function to update the number of stair treads
    updateStairTreads(numTreads) {
        this.numTreads = numTreads;
        this.createStaircase(numTreads);
        this.updateWallsWithBoundingBox();
    }

    // Function to update the tread width
    updateTreadWidth(width) {
        const widthScale = width / 800;  // Adjust the scaling factor based on the width
        this.treadGroup.children.forEach(tread => {
            const treadElement = tread.getObjectByName('Tread');
            if (treadElement) {
                treadElement.scale.x = widthScale;  // Scale only the 'Tread' part
            }
        });
        this.updateWallsWithBoundingBox();
    }
    updateWallsWithBoundingBox() {
        // Calculate bounding box of the staircase treads
        const bbox = new THREE.Box3().setFromObject(this.treadGroup);
        const size = bbox.getSize(new THREE.Vector3());
        const center = bbox.getCenter(new THREE.Vector3());
        
    
        // Check for valid size and center values
        if (!isFinite(size.x) || !isFinite(size.y) || !isFinite(size.z) ||
            !isFinite(center.x) || !isFinite(center.y) || !isFinite(center.z)) {
            console.error('Invalid size or center calculations for bounding box:', size, center);
            return; // Prevent further execution if bounding box calculations are invalid
        }
    
        // Get cantilever status
        const isCantilever = document.querySelector('input[name="cantilever-option"]:checked').value === 'cantilever';
    
        // Use cantileverWallHeight if cantilever is enabled, otherwise use default wallHeight
        const wallThickness = 3; // Assuming this is defined elsewhere or passed as a parameter
    
        // Adjust back wall width and height based on cantilever status
        const backWallWidth = size.x + (wallThickness * 2); // Including some offset if required
        const backWallHeight = 1.2; // Using the height determined by cantilever status

        const wallOffset = isCantilever ? 0 : 0.2;
    
        if (!isFinite(backWallWidth) || !isFinite(backWallHeight) || !isFinite(wallThickness)) {
            console.error('Invalid dimensions for wall geometry:', backWallWidth, backWallHeight, wallThickness);
            return; // Prevent geometry creation if dimensions are invalid
        }
    
        // Update back wall geometry and position
        this.walls.backWall.geometry.dispose();
        this.walls.backWall.geometry = new THREE.BoxGeometry(backWallWidth, backWallHeight, wallThickness);
        this.walls.backWall.position.set(
            center.x,  // Center of the staircase
            center.y + size.y / 2 - backWallHeight / 2,  // Positioned at the top of the staircase
            center.z + size.z / 2 + wallThickness / 2  - 0.2 // Assuming some offset
        );
    
        // Update side wall geometry and position
        const sideWallHeight = size.y;  // Height of the side walls to match the bounding box height
        const sideWallLength = size.z;  // Length of the side walls to match the bounding box depth
    
        if (!isFinite(sideWallHeight) || !isFinite(sideWallLength) || !isFinite(wallThickness)) {
            console.error('Invalid dimensions for side wall geometry:', wallThickness, sideWallHeight, sideWallLength);
            return; // Prevent geometry creation if dimensions are invalid
        }
    
        this.walls.sideWall.geometry.dispose();
        this.walls.sideWall.geometry = new THREE.BoxGeometry(wallThickness, sideWallHeight, sideWallLength);
        this.walls.sideWall.position.set(
            bbox.min.x - wallThickness / 2 - wallOffset,  // Positioned to the left of the staircase
            center.y,  // Centered vertically
            center.z  // Centered along the Z-axis
        );
    }
    

    // Function to clear existing treads
    clearTreads() {
        while (this.treadGroup.children.length > 0) {
            const tread = this.treadGroup.children.pop();
            this.scene.remove(tread);
        }
    }

    // Function to create wall material
    createWallMaterial() {
        const textureLoader = new THREE.TextureLoader();
        const wallTexture = textureLoader.load('/textures/cross-laminated-timber-clt-fir-2000-mm-architextures.jpg');
        wallTexture.wrapS = THREE.RepeatWrapping;
        wallTexture.wrapT = THREE.RepeatWrapping;
        wallTexture.repeat.set(4, 4);

        return new THREE.MeshStandardMaterial({
            map: wallTexture,
            side: THREE.DoubleSide,
        });
    }
}
